import * as THREE from 'three';
import type { CameraController } from './CameraController';

export interface FollowConfig {
  readonly offsetBehind: number;
  readonly offsetUp: number;
  readonly lookAhead: number;
  readonly positionLerp: number;
  readonly lookLerp: number;
  readonly lookHeight?: number;
  /** Tighter behind-offset reached at full thrust. Camera lerps between
   *  offsetBehind (rest) and closeOffsetBehind by getCloseFactor(). */
  readonly closeOffsetBehind?: number;
  /** 0..1 factor: 0 = full offsetBehind, 1 = closeOffsetBehind. Sampled
   *  every frame so the camera tightens when the throttle is up. */
  readonly getCloseFactor?: () => number;
}

export class FollowController implements CameraController {
  private readonly smoothedLook: THREE.Vector3;
  private readonly tmpOffset = new THREE.Vector3();
  private readonly tmpDesired = new THREE.Vector3();
  private readonly tmpAhead = new THREE.Vector3();

  constructor(
    private readonly camera: THREE.Camera,
    private readonly target: THREE.Object3D,
    private readonly config: FollowConfig
  ) {
    this.camera.position.copy(this.computeDesiredPosition(this.tmpDesired));
    this.smoothedLook = this.computeLookTarget(new THREE.Vector3());
    this.camera.lookAt(this.smoothedLook);
  }

  update(dt: number): void {
    const desired = this.computeDesiredPosition(this.tmpDesired);
    const posAlpha = 1 - Math.exp(-this.config.positionLerp * dt);
    this.camera.position.lerp(desired, posAlpha);

    const look = this.computeLookTarget(this.tmpAhead);
    const lookAlpha = 1 - Math.exp(-this.config.lookLerp * dt);
    this.smoothedLook.lerp(look, lookAlpha);
    this.camera.lookAt(this.smoothedLook);
  }

  /** Skip the lerps and place the camera + look target at their desired
   *  values immediately. Used by the hyperspace jump where chase-cam
   *  velocity (650 u/s) overruns positionLerp/lookLerp by ~100u of lag,
   *  flipping the look direction to face the ship's tail. */
  snapToTarget(): void {
    this.camera.position.copy(this.computeDesiredPosition(this.tmpDesired));
    this.smoothedLook.copy(this.computeLookTarget(this.tmpAhead));
    this.camera.lookAt(this.smoothedLook);
  }

  private computeDesiredPosition(out: THREE.Vector3): THREE.Vector3 {
    const factor = this.config.getCloseFactor?.() ?? 0;
    const close = this.config.closeOffsetBehind ?? this.config.offsetBehind;
    const offsetBehind =
      this.config.offsetBehind +
      (close - this.config.offsetBehind) * Math.max(0, Math.min(1, factor));
    this.tmpOffset
      .set(0, this.config.offsetUp, offsetBehind)
      .applyQuaternion(this.target.quaternion);
    return out.copy(this.target.position).add(this.tmpOffset);
  }

  private computeLookTarget(out: THREE.Vector3): THREE.Vector3 {
    const lookHeight = this.config.lookHeight ?? 0;
    out
      .set(0, lookHeight, -this.config.lookAhead)
      .applyQuaternion(this.target.quaternion);
    return out.add(this.target.position);
  }
}
